import { useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { contactPhoneNumberQuery } from "../queries";
import { setContactPhoneNumberMutation } from "../commands";

export interface ContactPhoneNumberState {
  readonly phoneNumber: string | null;
  /** No number to show yet, not even a cached copy. */
  readonly isLoading: boolean;
  /** Showing a cached copy while the fresh one loads; the field shouldn't be edited until it lands. */
  readonly isRefreshing: boolean;
  readonly isError: boolean;
  /** Replaces the phone number, or clears it when given `null`. Rejects on an invalid (non-international) format. */
  readonly setPhoneNumber: (phoneNumber: string | null) => Promise<void>;
}

/**
 * The calling user's phone number for SMS notification delivery — a single,
 * unscoped value (unlike `useNotificationPreferences`'s per-camera/site
 * cascade), since a phone number belongs to the person, not to any one scope.
 */
export function useContactPhoneNumber(): ContactPhoneNumberState {
  const apiConfig = useApiConfig();
  const queryClient = useQueryClient();

  const queryOptions = contactPhoneNumberQuery(apiConfig, {});
  // Always refetch on mount: the persisted cache may hold a number changed
  // since, e.g. from another device or by support on the user's behalf.
  const { data, isLoading, isError, isFetching, isFetchedAfterMount } = useQuery({ ...queryOptions, staleTime: 0 });

  const mutationConfig =
    apiConfig.tenant ?
      {
        tenantId: apiConfig.tenant.tenantId,
        apiBaseUrl: apiConfig.tenant.apiBaseUrl,
        authenticatedFetch: apiConfig.authenticatedFetch,
      }
    : { authenticatedFetch: apiConfig.authenticatedFetch };

  const mutation = useMutation(setContactPhoneNumberMutation(mutationConfig, {}));
  // Saves run one at a time, in the order they were made. Sent concurrently,
  // two PUTs could reach the server out of order, and an earlier save's
  // response could land after a later one's and overwrite it in the cache —
  // e.g. a country change resurrecting the number the user then cleared.
  const queueRef = useRef<Promise<unknown>>(Promise.resolve());

  const save = async (phoneNumber: string | null): Promise<void> => {
    const result = await mutation.mutateAsync({ phoneNumber });
    // Cancel any GET for this query still in flight from mount before
    // writing the fresh save result into the cache — otherwise a slower
    // stale response landing afterward would overwrite it, snapping the
    // field back to the pre-save value even though the server already
    // accepted the new one.
    await queryClient.cancelQueries({ queryKey: queryOptions.queryKey });
    queryClient.setQueryData(queryOptions.queryKey, result);
  };

  const setPhoneNumber = (phoneNumber: string | null): Promise<void> => {
    const next = queueRef.current.then(() => save(phoneNumber));
    // A failed save rejects its own caller, but mustn't stop the next one.
    queueRef.current = next.catch(() => undefined);
    return next;
  };

  return {
    phoneNumber: data?.phoneNumber ?? null,
    isLoading,
    isRefreshing: isFetching && !isFetchedAfterMount,
    isError,
    setPhoneNumber,
  };
}
